
import React from "react";
import { Card, CardContent, Typography, Box, LinearProgress, Chip, Avatar, AvatarGroup, Divider, styled, linearProgressClasses, Tooltip } from "@mui/material";

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
  },
  progressBar: {
    flexGrow: 1,
    height: "16px",
    backgroundColor: "#d9d9d9", // Grey background (represents remaining)
    borderRadius: "8px",
    position: "relative",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#4caf50", // Green (completed part)
    borderRadius: "8px 0 0 8px",
    transition: "width 0.3s ease-in-out",
  },
  label: {
    minWidth: "40px",
    fontWeight: "bold",
  },
};


const ProgressBar = ({ progress }) => {
  return (
    <div style={styles.container}>
      {/* Background bar (grey = 100% width) */}
      <div style={styles.progressBar}>
        {/* Filled part (green = progress%) */}
        <div
          style={{
            ...styles.progressFill,
            width: `${progress}%`,
          }}
        />
      </div>

      {/* Show percentage value */}
      <span style={styles.label}>{progress}</span>
    </div>
  );
};

const CustomLinearProgress = ({ value, height = 7, backgroundColor = "#e0e0e0", fillColor = "green" }) => {
  // Clamp the value between 0 and 100
  const safeValue = Math.min(100, Math.max(0, value));

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: backgroundColor,
        borderRadius: "8px",
        overflow: "hidden",
        height: `${height}px`,
      }}
    >
      <div
        style={{
          width: `${safeValue}%`,
          backgroundColor: fillColor,
          height: "100%",
          transition: "width 0.3s ease-in-out",
        }}
      />
    </div>
  );
};


const DashboardProjectCard = ({
  projectName,
  projectCode,
  manager,
  status,
  priority,
  date,
  progress,
  projectGulanar,
  startDate,
  endDate,
}) => {
  // Status color mapping
  const statusColor = {
    "In Progress": "orange",
    Completed: "green",
    Pending: "gray",
    Hold: 'red',
    "Not Started": "blue",
  };

  const today = new Date();
  const dateToday = today.getDate();
  const currenMonth = today.getMonth() + 1; // Months are zero-based
  const year = today.getFullYear();

  const maxVisible = 4; // max avatars to show
  const visibleEmployees = projectGulanar.slice(0, maxVisible);
  const remaining = projectGulanar.length - maxVisible;


  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3, width: "97%" }}  >
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600} color="#000000">
              {projectName}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", }}>
            <Typography variant="body2" color="#124580" sx={{ fontSize: "13px" }}>
              {projectCode}
            </Typography>
            <Typography variant="subtitle2" color="#124580" sx={{ fontSize: "13px" }}>
              {manager}
            </Typography>
          </Box>
        </Box>

        <Divider />


        {/* Status & Priority */}
        <Box display="flex" justifyContent="space-between" alignItems="center" >
          <Typography variant="body2" sx={{ color: statusColor[status] || "black", fontSize: "12px" }}>
            ● {status}
          </Typography>
          <Chip
            label={priority}
            size="small"
            variant="outlined"
            sx={{ bgcolor: "#fff4e5", color: "#ed6c02", fontSize: "0.65rem" }}
          />
        </Box>

        {/* Progress */}
        <Box mb={1} >
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
              {dateToday}-{currenMonth}-{year}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>{progress}</Typography>
          </Box>
          {/* <Box sx={{ width: "100%", height: 8, borderRadius: 10, overflow: "hidden", background: "lightgrey" }}>
            <Box sx={{ height: "100%", width: "60%", backgroundColor: "orange", borderRadius: 12, }}>
            </Box>
          </Box> */}
          <CustomLinearProgress value={parseInt(progress)} />
        </Box>

        {/* Members */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AvatarGroup max={maxVisible}>
            {visibleEmployees.map((m, idx) => (
              <Tooltip key={idx} title={m.employee_name} arrow>
                <Avatar
                  alt={m.employee_name}
                  src={m.avatar || undefined}
                  sx={{ bgcolor: "#2D5F99", fontSize: "12px" }}
                >
                  {!m.avatar && m.employee_name?.charAt(0).toUpperCase()}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>

          {/* Show remaining count if more than maxVisible */}
          {remaining > 0 && (
            <Typography sx={{ fontSize: "12px", color: "#2D5F99" }}>
              + {remaining} Others
            </Typography>
          )}
        </Box>

        <Divider />
        {/* Dates */}
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" >
            <b style={{ color: "#000000" }}>Start Date:</b> {startDate}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <b style={{ color: "#000000" }}>End Date:</b>  {endDate}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DashboardProjectCard;
